/**
 * Leitura e escrita de campos binarios.
 *
 * Usa `DataView` em vez de indexar `Uint8Array` direto. Com
 * `noUncheckedIndexedAccess`, cada `bytes[i]` obriga a um `?? 0` que nunca
 * acontece na pratica mas conta como um ramo nao testado — e, pior, esconde
 * um erro de limite em vez de deixa-lo aparecer. O `DataView` lanca quando
 * a leitura sai do buffer, que e o comportamento certo para um parser que
 * recebe arquivo de fora.
 */
import { DisguiseError } from './types.js'

export function view(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
}

export function readU32BE(bytes: Uint8Array, offset: number): number {
  requireRange(bytes, offset, 4)
  return view(bytes).getUint32(offset, false)
}

export function readU32LE(bytes: Uint8Array, offset: number): number {
  requireRange(bytes, offset, 4)
  return view(bytes).getUint32(offset, true)
}

export function requireRange(bytes: Uint8Array, offset: number, length: number): void {
  if (offset < 0 || length < 0 || offset + length > bytes.length) {
    throw new DisguiseError(`leitura fora do buffer: ${offset}+${length} de ${bytes.length}`)
  }
}

export function u32be(value: number): Uint8Array {
  const out = new Uint8Array(4)
  view(out).setUint32(0, value, false)
  return out
}

export function u32le(value: number): Uint8Array {
  const out = new Uint8Array(4)
  view(out).setUint32(0, value, true)
  return out
}

export function u16le(value: number): Uint8Array {
  const out = new Uint8Array(2)
  view(out).setUint16(0, value, true)
  return out
}

/** Texto ASCII → bytes. Rotulos de formato (RIFF, IHDR) sao sempre ASCII. */
export function ascii(text: string): Uint8Array {
  const out = new Uint8Array(text.length)
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i)
    if (code > 0x7f) throw new DisguiseError(`rotulo nao-ASCII: ${text}`)
    out[i] = code
  }
  return out
}

/** Le um rotulo de 4 bytes (id de chunk PNG ou RIFF). */
export function tagAt(bytes: Uint8Array, offset: number): string {
  requireRange(bytes, offset, 4)
  const dv = view(bytes)
  return String.fromCharCode(
    dv.getUint8(offset),
    dv.getUint8(offset + 1),
    dv.getUint8(offset + 2),
    dv.getUint8(offset + 3),
  )
}

export function concatBytes(parts: readonly Uint8Array[]): Uint8Array {
  let total = 0
  for (const p of parts) total += p.length
  const out = new Uint8Array(total)
  let pos = 0
  for (const p of parts) {
    out.set(p, pos)
    pos += p.length
  }
  return out
}

/** Texto → bytes, e de volta, para inspecionar conteudo de arquivo em teste. */
export function bytesToLatin1(bytes: Uint8Array): string {
  let out = ''
  const dv = view(bytes)
  for (let i = 0; i < bytes.length; i++) out += String.fromCharCode(dv.getUint8(i))
  return out
}
